import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SignalRService } from './signalr.service';
import {
  ICurrentUserModel,
  ILocationOption,
  ILoginResponseModel,
} from '@models/auth/auth.model';
import { StorageService } from './storage.service';
import {
  COOKIE_EXPIRY_DAYS,
  CURRENT_USER,
} from '@core/constants/session.const';
import { IRolePermisssionModel } from '@models/module/module.model';
import { DataHelper, stringAvatar } from '@core/helpers/data.helper';
import { AppModule } from '@core/enums/app-module.enum';
import { UserType } from '@core/enums/type.enum';
import { Routing } from '@core/constants/routing.const';
import { Router } from '@angular/router';
import { LocationFilterService } from './location-filter.service';

const DASHBOARD_PERMISSIONS = {
  RoleId: 0,
  ModuleName: 'Dashboard',
  Description: 'Dashboard',
  CanManage: true,
  CanView: true,
  CanDelete: true,
};
const MESSAGES_PERMISSIONS = {
  RoleId: 0,
  ModuleName: 'Message',
  Description: 'Messages',
  CanManage: true,
  CanView: true,
  CanDelete: true,
};
const BROADCAST_PERMISSIONS = {
  RoleId: 0,
  ModuleName: 'Broadcast',
  Description: 'Broadcast',
  CanManage: true,
  CanView: true,
  CanDelete: true,
};
const PROFILE_PERMISSIONS = {
  RoleId: 0,
  ModuleName: 'Profile',
  Description: 'Profile',
  CanManage: true,
  CanView: true,
  CanDelete: true,
};

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  IsUserLoggedIn: BehaviorSubject<boolean>;
  rememberMe = false;
  cookieExpiryDays = COOKIE_EXPIRY_DAYS;
  private cachedUser: ICurrentUserModel | null = null;
  private isLoaded = false;
  private initPromise!: Promise<void>;

  constructor(
    private storageService: StorageService, // private cookieService: CookieService
    private router: Router,
    private injector: Injector
  ) {
    this.IsUserLoggedIn = new BehaviorSubject(false);
    this.initPromise = this.loadInitialUser();
  }

  private async loadInitialUser() {
    try {
      const val = await this.storageService.get(CURRENT_USER);
      // Only apply if no user was set in-memory during the async gap (e.g. concurrent login)
      if (this.cachedUser === null) {
        if (val) {
          this.cachedUser = JSON.parse(val);
          this.IsUserLoggedIn.next(true);
        } else {
          this.cachedUser = null;
        }
      }
    } catch {
      if (this.cachedUser === null) this.cachedUser = null;
    } finally {
      this.isLoaded = true;
    }
  }

  private async set(val: string) {
    if (val) {
      this.cachedUser = JSON.parse(val);
      this.IsUserLoggedIn.next(true);
    } else {
      this.cachedUser = null;
    }
    await this.storageService.set(CURRENT_USER, val);
  }

  get(): any {
    return this.cachedUser;
  }

  async getAsync(): Promise<any> {
    await this.initPromise;
    return this.cachedUser;
  }

  setCurrentUser(responseBody: ILoginResponseModel) {
    const permissions = this.getRolePermissions(
      responseBody?.permissions,
      responseBody?.userTypeId
    );

    const currentUser: ICurrentUserModel = { ...responseBody, permissions };
    this.set(JSON.stringify(currentUser));
  }

  /**
   * Patches only the token fields in the stored session after a silent refresh.
   * All other user fields (permissions, userName, etc.) remain unchanged.
   */
  updateTokens(jwtToken: string, refreshToken: string, expiration: Date): void {
    const currentUser: ICurrentUserModel = this.get();
    if (!currentUser) return;
    const updated: ICurrentUserModel = { ...currentUser, jwtToken, refreshToken, expiration };
    this.set(JSON.stringify(updated));
  }

  updateCurrentUserPermissions(permissions: any[]): void {
    const currentUser: ICurrentUserModel = this.get();
    if (!currentUser) return;
    const updated: ICurrentUserModel = { ...currentUser, permissions };
    this.set(JSON.stringify(updated));
  }

  getRolePermissions(permissionStr: string, userTypeId: number) {
    let permissions: Array<IRolePermisssionModel> = DataHelper.IsJsonString(
      permissionStr
    )
      ? JSON.parse(permissionStr)
      : [];
    if (userTypeId === UserType.SuperAdmin) {
      permissions = Object.keys(AppModule)?.map((menu) => {
        return {
          ModuleName: menu,
          CanManage: true,
          CanView: true,
          CanDelete: true,
        };
      });
    } else {
      permissions.push(DASHBOARD_PERMISSIONS);
      permissions.push(MESSAGES_PERMISSIONS);
      permissions.push(BROADCAST_PERMISSIONS);
      permissions.push(PROFILE_PERMISSIONS);
    }
    return permissions;
  }

  getShortName = () => stringAvatar(this.get()?.userName || 'A B');

  getUserName = () => this.get()?.userName;

  setUserName = (userName: string) => {
    const currentUser: ICurrentUserModel = this.get() || ({} as ICurrentUserModel);
    currentUser.userName = userName;
    this.set(JSON.stringify(currentUser));
  };

  setLocations = (locations: ILocationOption[]) => {
    const currentUser: ICurrentUserModel = this.get() || ({} as ICurrentUserModel);
    currentUser.locations = locations;
    this.set(JSON.stringify(currentUser));
  };

  setProfessionalDescription = (professionalDescription: string) => {
    const currentUser: ICurrentUserModel = this.get() || ({} as ICurrentUserModel);
    currentUser.professionalDescription = professionalDescription;
    this.set(JSON.stringify(currentUser));
  };

  logout = async () => {
    this.cachedUser = null;
    this.IsUserLoggedIn.next(false);
    await this.storageService.remove(CURRENT_USER);
    try {
      const signalRService = this.injector.get(SignalRService);
      signalRService.stopConnection();
    } catch (err) {
      console.error('Error stopping SignalR connection on logout:', err);
    }
    try {
      const locationFilterService = this.injector.get(LocationFilterService);
      locationFilterService.clear();
    } catch (err) {
      console.error('Error clearing LocationFilterService on logout:', err);
    }
    this.router.navigateByUrl(`/${Routing.Auth}/${Routing.Login}`);
  };
}
